import { SxProps, Theme } from '@mui/system';
import React, { memo, useCallback } from 'react';
import type { Calendar, Location, Unit } from 'sr-types/lib/search/v1/graphql';
import FormPanel from '../../form/FormPanel';
import { useShallowMergeSxProps } from '../../hooks/useShallowMergeSxProps';
import ScheduleFormCompactWidget from './components/ScheduleFormCompactWidget';
import ScheduleFormStandardDateOnlyWidget from './components/ScheduleFormStandardDateOnlyWidget';
import ScheduleFormStandardWidget from './components/ScheduleFormStandardWidget';
import ScheduleFormWidgetProvider from './ScheduleFormWidgetProvider';
import { ScheduleFormWidgetValue, SupportedSchedulerDurationUnit } from './types';

export interface ScheduleFormWidgetProps {
    /** The unique identifier for this widget instance */
    id: string;

    /** The controlled value of the ScheduleFormWidget */
    value: ScheduleFormWidgetValue;

    /**
     * Callback fired when the value of the ScheduleFormWidget changes.
     * The consumer should send a memoized callback to prevent unnecessary re-renders.
     */
    onChange: (value: ScheduleFormWidgetValue, id: string) => void;

    /** Disables the entire widget when true. */
    disabled?: boolean;

    /** An optional list of duration units to limit the options for the duration unit selector. */
    allowedDurationUnits?: SupportedSchedulerDurationUnit[];

    /** Allowed locations to constrain the location picker */
    allowedLocations?: Array<Location>;

    /** Allow additional locations beyond allowedLocations (default: false) */
    allowAdditionalLocations?: boolean;

    /** Allowed calendars to constrain the calendar picker */
    allowedCalendars?: Array<Calendar>;

    /**
     * Optional: Pre-fetched duration units data. If provided, skips the internal API call.
     * Used to avoid duplicate API calls when parent component already has units data.
     */
    durationUnits?: Unit[];

    /**
     * Layout variant (default: 'standard')
     * - 'standard': Full vertical layout with name field hidden
     * - 'standard-date-only': Standard layout without location/calendar fields
     * - 'compact': Compact 2-line layout with name field visible
     * - 'compact-sequence-follower': Compact 2-line layout for sequence followers with limited enabled fields
     */
    variant?: 'standard' | 'standard-date-only' | 'compact' | 'compact-sequence-follower';

    /**
     * Callback fired when the calculation state changes.
     * Called with `true` when calculation starts, `false` when it completes or fails.
     * Useful for showing loading indicators in parent components.
     */
    onCalculationStateChange?: (isCalculating: boolean) => void;

    /**
     * Optional i18n token for the label of the "Use Duration" form field.
     * Defaults to 'scheduleFormWidget.useDuration.label'.
     */
    specifyDurationFormFieldLabelToken?: string;

    /**
     * Optional i18n token for the label of the name form field.
     * Defaults to 'scheduleFormWidget.name.label'.
     */
    nameFormFieldLabelToken?: string;

    /**
     * Optional MUI sx prop for custom styling.
     */
    sx?: SxProps<Theme>;
}

function ScheduleFormWidgetNonMemo({
    id,
    value,
    onChange,
    allowedDurationUnits,
    durationUnits,
    disabled = false,
    allowedLocations,
    allowAdditionalLocations,
    allowedCalendars,
    variant = 'standard',
    onCalculationStateChange,
    specifyDurationFormFieldLabelToken = 'scheduleFormWidget.useDuration.label',
    nameFormFieldLabelToken = 'scheduleFormWidget.name.label',
    sx
}: ScheduleFormWidgetProps) {
    const handleValueChange = useCallback(
        (value: ScheduleFormWidgetValue) => {
            onChange(value, id);
        },
        [id, onChange]
    );

    const mergedSxProp = useShallowMergeSxProps({ p: 0 }, sx);

    return (
        <ScheduleFormWidgetProvider
            mode="stand-alone-schedule"
            value={value}
            onChange={handleValueChange}
            disabled={disabled}
            allowedDurationUnits={allowedDurationUnits}
            durationUnits={durationUnits}
            allowedLocations={allowedLocations}
            allowAdditionalLocations={allowAdditionalLocations}
            allowedCalendars={allowedCalendars}
            onCalculationStateChange={onCalculationStateChange}
            nameFormFieldLabelToken={nameFormFieldLabelToken}
            specifyDurationFormFieldLabelToken={specifyDurationFormFieldLabelToken}
        >
            <FormPanel sx={mergedSxProp}>
                {variant === 'standard' ? (
                    <ScheduleFormStandardWidget />
                ) : variant === 'standard-date-only' ? (
                    <ScheduleFormStandardDateOnlyWidget />
                ) : (
                    <ScheduleFormCompactWidget isSequenceFollower={variant === 'compact-sequence-follower'} />
                )}
            </FormPanel>
        </ScheduleFormWidgetProvider>
    );
}

ScheduleFormWidgetNonMemo.displayName = 'ScheduleFormWidget';
const ScheduleFormWidget = memo(ScheduleFormWidgetNonMemo);
export default ScheduleFormWidget;
